<template>
	<div v-if="elements.length" class="c-accordion-layout">
		<!-- Title area -->
		<div
			class="
				>=768:flex >=768:justify-between >=768:items-start
				align-baseline
				>=768:gap-(1/4)/12col
				mb-2xs/v
			"
		>
			<div v-if="title" class="flex-shrink flex-grow">
				<slot name="title">
					<BaseH2 v-text="title" />
				</slot>
			</div>

			<BaseButton
				v-if="elements.length > 1"
				class="
					ml-auto
					mt-16
					>=768:mt-0
					whitespace-nowrap
					justify-between
					flex-shrink-0 flex-grow-0
				"
				:class="[
					'bg-white hover:bg-theme-10 text-theme border border-current',
				]"
				@click="() => toggleAll()"
			>
				<!-- To avoid the button from changing size -->
				<span
					:key="isGroupOpen ? 'open' : 'closed'"
					class="absolute"
					v-text="isGroupOpen ? 'Luk alle' : 'Åben alle'"
				></span>
				<span
					aria-hidden="true"
					class="invisible"
					v-text="'Åben alle'"
				></span>

				<SvgIconWedge
					:class="[
						'ml-1em w-12 h-8 mt-1',
						'transform duration-200 ease-smooth-out',
						{ '-rotate-180 -mt-1': isGroupOpen },
					]"
				/>
			</BaseButton>
		</div>

		<!-- Accordion -->
		<AccordionGroup
			v-bind="{ tag, minOneOpen, maxOneOpen }"
			ref="accordionGroup"
			class="overflow-hidden isolate"
		>
			<div
				v-for="(item, index) in elements"
				:key="`item-${index}`"
				:class="[
					'c-accordion-layout__item',
					panelsOpen[index]
						? 'c-accordion-layout__item--open'
						: 'c-accordion-layout__item--closed',
					'relative border-t border-b >=1024:border border-gray-medium',
					'transition-colors duration-200 ease-smooth-out',
					{
						'bg-theme >=1024:bg-white': !panelsOpen[index],
						'bg-white >=1024:bg-theme-20 >=1024:bg-opacity-20':
							panelsOpen[index],
						'-mt-1': index > 0,
					},
				]"
			>
				<!-- Header -->
				<AccordionHeader
					:id="`accordion-${item.key}`"
					:key="`accordion-${item.key}`"
					:aria-controls="`panel-${item.key}`"
					:class="[
						'group',
						'w-full px-sm/h py-xs/v',
						'transition-colors duration-100 ease-smooth-out',
						{
							'text-white >=1024:text-text': !panelsOpen[index],
							'text-theme': panelsOpen[index],
						},
					]"
				>
					<div
						class="flex justify-between items-center align-baseline"
					>
						<div
							:class="[
								'pr-(1/4)/12col >=1024:pr-xs/h',
								'transform duration-200 ease-smooth-out',
								{
									'>=1024:translate-x-2xs/h':
										panelsOpen[index],
								},
							]"
						>
							<slot
								name="header"
								v-bind="{
									item,
									index,
									isActive: panelsOpen[index],
								}"
							>
								<BaseH3
									class="text-card-title-sm font-light"
									v-text="item.content.title"
								/>
							</slot>
						</div>

						<BaseButton
							tag="div"
							:class="[
								'w-40 h-40 p-0 flex-shrink-0',
								'flex items-center justify-center',
								'rounded-full border >=1024:border-theme',
								'hover:bg-theme-90 group-hover:bg-theme-90',
								'transition duration-200 ease-in-out',
								{
									'>=1024:bg-theme >=1024:text-white transform -rotate-180':
										panelsOpen[index],
									'>=1024:bg-white >=1024:hover:bg-theme-10 >=1024:group-hover:bg-theme-10 >=1024:text-theme':
										!panelsOpen[index],
								},
							]"
						>
							<SvgIconWedge
								class="w-12 h-8 transform translate-y-1"
							/>
						</BaseButton>
					</div>
				</AccordionHeader>

				<!-- Panel -->
				<AccordionPanel
					:id="`panel-${item.key}`"
					:ref="`panel-${index}`"
					:key="`panel-${item.key}`"
					:transition="{ name: 't-accordion' }"
					class="px-sm/h"
					@change="(event) => onPanelChange(event, index)"
				>
					<div
						:class="[
							'transform >=1024:pr-2xs/h',
							'duration-200 ease-smooth-out',
							{
								'>=1024:translate-x-2xs/h': panelsOpen[index],
							},
						]"
					>
						<div class=">=1024:pr-2xs/h pb-lg/v">
							<slot
								name="panel"
								v-bind="{
									item,
									index,
									isActive: panelsOpen[index],
								}"
							></slot>
						</div>
					</div>
				</AccordionPanel>
			</div>
		</AccordionGroup>
	</div>
</template>

<script>
import {
	AccordionGroup,
	AccordionHeader,
	AccordionPanel,
} from '~/components/shared/Accordion';
import SvgIconWedge from '~/assets/svgs/icon-wedge.svg?inline';

export default {
	name: 'AccordionLayout',

	components: {
		AccordionGroup,
		AccordionHeader,
		AccordionPanel,
		SvgIconWedge,
	},

	provide: {
		headingLevel: 3,
	},

	props: {
		...AccordionGroup.props,

		title: {
			type: String,
			required: false,
		},

		elements: {
			type: Array,
			default: () => [],
		},
	},

	data() {
		return {
			panelsOpen: this.elements.map(() => false),
			isGroupOpen: false,
		};
	},

	mounted() {
		if (window.location.hash) {
			try {
				const el = document.querySelector(window.location.hash);
				el?.scrollIntoView(true);
			} catch (err) {
				console.error(err);
			}
		}
	},

	methods: {
		toggleAll(force = null) {
			if (this.$refs.accordionGroup) {
				const { accordionGroup: ag } = this.$refs;
				const state = force !== null ? force : !this.isGroupOpen;
				!state ? ag.closeAll() : ag.openAll();
			}
		},
		openPanel(index) {
			if (this.$refs[`panel-${index}`][0]?.open) {
				this.$refs[`panel-${index}`][0].open();
			}
		},
		onPanelChange(event, index) {
			this.$set(this.panelsOpen, index, event.isOpen);
			this.$emit('change', { index, state: event.isOpen });

			this.isGroupOpen =
				this.panelsOpen.reduce(
					(acc, cur) => (cur ? acc + 1 : acc),
					0
				) >= 2;
		},
	},
};
</script>

<style lang="postcss">
.c-accordion-layout__item:before {
	@apply pointer-events-none border border-transparent;
	@apply block absolute -left-1 -top-1 z-10;
	@apply transition-colors duration-200 ease-smooth-out;

	content: '';
	width: calc(100% + 2px);
	height: calc(100% + 2px);
	@apply <1024:left-0 <1024:w-full;
}
.c-accordion-layout__item--open:before {
	@apply border-theme-20;
}
</style>
